import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { annualReconciliationAPI, contractAPI, propertyAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import '../Contract/ContractCreate.css';
import './AnnualReconciliationCreate.css';

const AnnualReconciliationCreate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Tab management
  const [activeTab, setActiveTab] = useState(1);
  const [tab1Saved, setTab1Saved] = useState(false);
  const [tab2Saved, setTab2Saved] = useState(false);
  const [tab3Saved, setTab3Saved] = useState(false);
  
  // Tab 1: Document & Property/Unit
  const [documentDate, setDocumentDate] = useState(new Date().toISOString().split('T')[0]);
  const [issuedIn, setIssuedIn] = useState('');
  const [propertyId, setPropertyId] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedUnitId, setSelectedUnitId] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [units, setUnits] = useState([]);
  const [propertyType, setPropertyType] = useState('');
  const [propertyDistrict, setPropertyDistrict] = useState('');
  const [plotNumber, setPlotNumber] = useState('');
  const [periodFrom, setPeriodFrom] = useState(new Date().toISOString().split('T')[0]);
  const [periodUntil, setPeriodUntil] = useState(new Date().toISOString().split('T')[0]);
  const [isActive, setIsActive] = useState(true);
  
  // Tab 2: Lessor (First Party)
  const [lessor, setLessor] = useState(null);
  const [propertyOwnerships, setPropertyOwnerships] = useState([]);
  const [firstPartyName, setFirstPartyName] = useState('');
  const [firstPartyIdNumber, setFirstPartyIdNumber] = useState('');
  const [firstPartyNationality, setFirstPartyNationality] = useState('');
  
  // Tab 3: Tenant (Second Party)
  const [secondPartyName, setSecondPartyName] = useState('');
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Check for selected property from PropertySelect page
  useEffect(() => {
    const selectedPropertyIdFromStorage = sessionStorage.getItem('selectedPropertyId');
    if (selectedPropertyIdFromStorage) {
      const propId = parseInt(selectedPropertyIdFromStorage);
      if (!isNaN(propId) && propId > 0) {
        loadSelectedProperty(propId);
        sessionStorage.removeItem('selectedPropertyId');
      }
    }
  }, []);

  // Load selected property details
  const loadSelectedProperty = async (propId) => {
    try {
      setLoading(true);
      const response = await contractAPI.getPropertiesWithDetails({ propertyId: parseInt(propId) });
      const data = response.data || response || {};
      
      let properties = [];
      if (Array.isArray(data)) {
        properties = data;
      } else if (data.properties) {
        properties = data.properties;
      } else if (data.items) {
        properties = data.items;
      }
      
      if (properties.length > 0) {
        const property = properties[0];
        setSelectedProperty(property);
        setPropertyId(parseInt(propId));
        
        // Auto-fill property information from selected property
        if (property.realEstateType) {
          setPropertyType(property.realEstateType.nameEng || property.realEstateType.NameEng || '');
        }
        if (property.district) {
          setPropertyDistrict(property.district.districtNameEng || property.district.DistrictNameEng || '');
        }
        if (property.realEstateNumber) {
          setPlotNumber(property.realEstateNumber || property.RealEstateNumber || '');
        }
        
        await loadPropertyUnits(propId);
        await loadPropertyOwnerships(propId);
      } else {
        try {
          const propResponse = await propertyAPI.getById(propId);
          if (propResponse.data) {
            setSelectedProperty(propResponse.data);
            setPropertyId(parseInt(propId));
            
            // Auto-fill property information
            if (propResponse.data.realEstateType) {
              setPropertyType(propResponse.data.realEstateType.nameEng || propResponse.data.realEstateType.NameEng || '');
            }
            if (propResponse.data.district) {
              setPropertyDistrict(propResponse.data.district.districtNameEng || propResponse.data.district.DistrictNameEng || '');
            }
            if (propResponse.data.realEstateNumber) {
              setPlotNumber(propResponse.data.realEstateNumber || propResponse.data.RealEstateNumber || '');
            }
            
            await loadPropertyUnits(propId);
            await loadPropertyOwnerships(propId);
          }
        } catch (fallbackErr) {
          console.error('Fallback property load failed:', fallbackErr);
          setErrorMessage('Failed to load property details.');
          setShowErrorModal(true);
        }
      }
    } catch (err) {
      console.error('Failed to load selected property:', err);
      try {
        const propResponse = await propertyAPI.getById(propId);
        if (propResponse.data) {
          setSelectedProperty(propResponse.data);
          setPropertyId(parseInt(propId));
          
          // Auto-fill property information
          if (propResponse.data.realEstateType) {
            setPropertyType(propResponse.data.realEstateType.nameEng || propResponse.data.realEstateType.NameEng || '');
          }
          if (propResponse.data.district) {
            setPropertyDistrict(propResponse.data.district.districtNameEng || propResponse.data.district.DistrictNameEng || '');
          }
          if (propResponse.data.realEstateNumber) {
            setPlotNumber(propResponse.data.realEstateNumber || propResponse.data.RealEstateNumber || '');
          }
          
          await loadPropertyUnits(propId);
          await loadPropertyOwnerships(propId);
        }
      } catch (fallbackErr) {
        console.error('Fallback property load failed:', fallbackErr);
        setErrorMessage('Failed to load property details.');
        setShowErrorModal(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPropertyUnits = async (propId) => {
    try {
      const response = await contractAPI.getPropertyUnits(propId);
      setUnits(response.data || []);
    } catch (err) {
      console.error('Failed to load property units:', err);
    }
  };

  const loadPropertyOwnerships = async (propId) => {
    try {
      const response = await contractAPI.getPropertyOwnerships(propId);
      setPropertyOwnerships(response.data || []);
    } catch (err) {
      console.error('Failed to load property ownerships:', err);
    }
  };

  // Handle Property Selection
  const handleSelectProperty = () => {
    navigate('/property/select', { state: { returnUrl: '/annual-reconciliation/create' } });
  };

  // Handle Unit Selection
  const handleUnitSelect = (unitId) => {
    const unit = units.find(u => (u.id || u.Id) === unitId);
    setSelectedUnitId(unitId);
    setSelectedUnit(unit);
  };

  // Handle Lessor Selection
  const handleLessorSelect = (ownershipId) => {
    const ownership = propertyOwnerships.find(o => (o.id || o.Id) === ownershipId);
    if (ownership) {
      setLessor({ propertyOwnershipId: ownershipId });
      setFirstPartyName(ownership.ownershipNameEng || ownership.OwnershipNameEng || ownership.ownershipNameAr || ownership.OwnershipNameAr || '');
      setFirstPartyIdNumber(ownership.identityNumber || ownership.IdentityNumber || ownership.crNumber || ownership.CrNumber || '');
      if (ownership.nationality) {
        setFirstPartyNationality(ownership.nationality.nameEng || ownership.Nationality?.NameEng || '');
      }
    }
  };

  // Check if tab can be accessed
  const canAccessTab = (tabNumber) => {
    if (tabNumber === 1) return true;
    if (tabNumber === 2) return tab1Saved;
    if (tabNumber === 3) return tab2Saved;
    return false;
  };

  // Handle tab click
  const handleTabClick = (tabNumber) => {
    if (!canAccessTab(tabNumber)) {
      return;
    }
    setActiveTab(tabNumber);
  };

  // Handle Next Tab
  const handleNextTab = () => {
    if (activeTab === 1) {
      // Validate Tab 1
      const newErrors = {};
      if (!documentDate) {
        newErrors.documentDate = t('annualReconciliation.create.errors.documentDateRequired');
      }
      if (!periodFrom) {
        newErrors.periodFrom = t('annualReconciliation.create.errors.periodFromRequired');
      }
      if (!periodUntil) {
        newErrors.periodUntil = t('annualReconciliation.create.errors.periodUntilRequired');
      }
      if (new Date(periodUntil) < new Date(periodFrom)) {
        newErrors.periodUntil = t('annualReconciliation.create.errors.periodUntilBeforePeriodFrom');
      }
      if (!propertyId) {
        newErrors.property = t('annualReconciliation.create.errors.propertyNameRequired') || 'Property selection is required';
      }
      
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }
      
      setTab1Saved(true);
      setActiveTab(2);
    } else if (activeTab === 2) {
      // Validate Tab 2
      const newErrors = {};
      if (!lessor || !firstPartyName.trim()) {
        newErrors.firstPartyName = t('annualReconciliation.create.errors.firstPartyNameRequired');
      }
      if (!firstPartyIdNumber.trim()) {
        newErrors.firstPartyIdNumber = t('annualReconciliation.create.errors.firstPartyIdNumberRequired');
      }
      
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }
      
      setTab2Saved(true);
      setActiveTab(3);
    }
  };

  // Handle Previous Tab
  const handlePreviousTab = () => {
    if (activeTab > 1) {
      setActiveTab(activeTab - 1);
    }
  };

  // Validate and submit
  const validate = () => {
    const newErrors = {};
    if (!documentDate) {
      newErrors.documentDate = t('annualReconciliation.create.errors.documentDateRequired');
    }
    if (!firstPartyName.trim()) {
      newErrors.firstPartyName = t('annualReconciliation.create.errors.firstPartyNameRequired');
    }
    if (!firstPartyIdNumber.trim()) {
      newErrors.firstPartyIdNumber = t('annualReconciliation.create.errors.firstPartyIdNumberRequired');
    }
    if (!periodFrom) {
      newErrors.periodFrom = t('annualReconciliation.create.errors.periodFromRequired');
    }
    if (!periodUntil) {
      newErrors.periodUntil = t('annualReconciliation.create.errors.periodUntilRequired');
    }
    if (new Date(periodUntil) < new Date(periodFrom)) {
      newErrors.periodUntil = t('annualReconciliation.create.errors.periodUntilBeforePeriodFrom');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      setActiveTab(1);
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        documentDate,
        issuedIn,
        firstPartyName,
        firstPartyIdNumber,
        firstPartyNationality,
        secondPartyName,
        propertyType,
        propertyDistrict,
        plotNumber,
        periodFrom,
        periodUntil,
        isActive,
      };
      await annualReconciliationAPI.create(submitData);
      setShowSuccessModal(true);
      setTimeout(() => {
        navigate('/annual-reconciliation');
      }, 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(err.response?.data?.message || t('annualReconciliation.create.messages.createError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <button className="btn-back" onClick={() => navigate('/annual-reconciliation')}>
        <i className="fas fa-arrow-left"></i> {t('annualReconciliation.create.backToList')}
      </button>

      <div className="property-form-container">
        {/* Tabs Navigation */}
        <div className="property-tabs">
          <div 
            className={`tab-header ${activeTab === 1 ? 'active' : ''} ${!canAccessTab(1) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(1)}
          >
            <i className="fas fa-building"></i>
            <span>{t('annualReconciliation.create.tabs.propertyUnit') || 'Property & Unit'}</span>
            {tab1Saved && <i className="fas fa-check-circle text-success ms-2"></i>}
          </div>
          <div 
            className={`tab-header ${activeTab === 2 ? 'active' : ''} ${!canAccessTab(2) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(2)}
          >
            <i className="fas fa-user-tie"></i>
            <span>{t('annualReconciliation.create.tabs.lessor') || 'Lessor'}</span>
            {tab2Saved && <i className="fas fa-check-circle text-success ms-2"></i>}
          </div>
          <div 
            className={`tab-header ${activeTab === 3 ? 'active' : ''} ${!canAccessTab(3) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(3)}
          >
            <i className="fas fa-user"></i>
            <span>{t('annualReconciliation.create.tabs.tenant') || 'Tenant'}</span>
            {tab3Saved && <i className="fas fa-check-circle text-success ms-2"></i>}
          </div>
        </div>

        {/* Tab Content */}
        <div className="property-tab-content">
          <form onSubmit={handleSubmit}>
            {/* Tab 1: Document & Property/Unit */}
            {activeTab === 1 && (
              <div className="tab-panel">
                <h3 className="tab-title">{t('annualReconciliation.create.tabs.propertyUnit') || 'Property & Unit Selection'}</h3>
                
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-calendar"></i> {t('annualReconciliation.create.fields.documentDate')} <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <i className="fas fa-calendar input-icon"></i>
                      <input
                        type="date"
                        className={`form-input ${errors.documentDate ? 'is-invalid' : ''}`}
                        value={documentDate}
                        onChange={(e) => setDocumentDate(e.target.value)}
                        required
                      />
                    </div>
                    {errors.documentDate && (
                      <div className="error-message">{errors.documentDate}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-map-marker-alt"></i> {t('annualReconciliation.create.fields.issuedIn')}
                    </label>
                    <div className="input-wrapper">
                      <i className="fas fa-map-marker-alt input-icon"></i>
                      <input
                        type="text"
                        className="form-input"
                        value={issuedIn}
                        onChange={(e) => setIssuedIn(e.target.value)}
                        placeholder={t('annualReconciliation.create.fields.issuedIn')}
                        maxLength={100}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-calendar"></i> {t('annualReconciliation.create.fields.periodFrom')} <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <i className="fas fa-calendar input-icon"></i>
                      <input
                        type="date"
                        className={`form-input ${errors.periodFrom ? 'is-invalid' : ''}`}
                        value={periodFrom}
                        onChange={(e) => setPeriodFrom(e.target.value)}
                        required
                      />
                    </div>
                    {errors.periodFrom && (
                      <div className="error-message">{errors.periodFrom}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-calendar"></i> {t('annualReconciliation.create.fields.periodUntil')} <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <i className="fas fa-calendar input-icon"></i>
                      <input
                        type="date"
                        className={`form-input ${errors.periodUntil ? 'is-invalid' : ''}`}
                        value={periodUntil}
                        onChange={(e) => setPeriodUntil(e.target.value)}
                        required
                      />
                    </div>
                    {errors.periodUntil && (
                      <div className="error-message">{errors.periodUntil}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-check-circle"></i> {t('annualReconciliation.create.fields.isActive')}
                    </label>
                    <div className="input-wrapper">
                      <i className="fas fa-check-circle input-icon"></i>
                      <select
                        className="form-input"
                        value={isActive ? 'true' : 'false'}
                        onChange={(e) => setIsActive(e.target.value === 'true')}
                      >
                        <option value="true">{t('common.yes')}</option>
                        <option value="false">{t('common.no')}</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Property Selection */}
                <div className="card mb-4">
                  <div className="card-header">
                    <h5><i className="fas fa-building text-primary"></i> <span className="text-primary">{t('contract.create.buttons.selectProperty') || 'Select Property'}</span></h5>
                  </div>
                  <div className="card-body">
                    {selectedProperty ? (
                      <div className="property-display-card">
                        <div className="row align-items-center">
                          <div className="col-md-10">
                            <div className="row mb-2">
                              <div className="col-12">
                                <h5 className="mb-1" style={{ display: 'inline-block', marginRight: '0.5rem' }}>
                                  {selectedProperty.nameEng || selectedProperty.NameEng}
                                </h5>
                                {(selectedProperty.nameAr || selectedProperty.NameAr) && (
                                  <span className="text-muted small" style={{ fontSize: '0.9rem' }}>
                                    - {selectedProperty.nameAr || selectedProperty.NameAr}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="row mb-2">
                              <div className="col-12 d-flex flex-wrap gap-3 align-items-center">
                                {(selectedProperty.code || selectedProperty.Code) && (
                                  <span className="small">
                                    <strong>{t('contract.create.fields.code') || 'Code'}:</strong> {selectedProperty.code || selectedProperty.Code}
                                  </span>
                                )}
                                {selectedProperty.realEstateType && (
                                  <span className="small">
                                    <strong>{t('contract.create.fields.type') || 'Type'}:</strong> {selectedProperty.realEstateType.nameEng || selectedProperty.realEstateType.NameEng}
                                  </span>
                                )}
                                {selectedProperty.city && (
                                  <span className="small">
                                    <strong>{t('contract.create.fields.city') || 'City'}:</strong> {selectedProperty.city.cityNameEng || selectedProperty.city.CityNameEng}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-2 text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={handleSelectProperty}
                            >
                              <i className="fas fa-edit"></i> {t('common.change') || 'Change'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSelectProperty}
                        >
                          <i className="fas fa-building"></i> {t('contract.create.buttons.selectProperty') || 'Select Property'}
                        </button>
                        {errors.property && (
                          <div className="error-message mt-2">{errors.property}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Unit Selection */}
                {propertyId && (
                  <div className="card mb-4">
                    <div className="card-header">
                      <h5><i className="fas fa-door-open text-primary"></i> <span className="text-primary">{t('contract.create.fields.selectUnits') || 'Select Unit'}</span></h5>
                    </div>
                    <div className="card-body">
                      {units.length === 0 ? (
                        <div className="alert alert-info">
                          <i className="fas fa-info-circle"></i> {t('contract.create.messages.noUnitsAvailable') || 'No units available for this property.'}
                        </div>
                      ) : (
                        <div className="units-grid-modern">
                          {units.map((unit) => {
                            const unitId = unit.id || unit.Id;
                            const isSelected = selectedUnitId === unitId;
                            return (
                              <div
                                key={unitId}
                                className={`unit-card-modern ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleUnitSelect(unitId)}
                              >
                                <div className="unit-card-header">
                                  <div className="unit-card-checkbox">
                                    <input
                                      type="radio"
                                      name="selectedUnit"
                                      checked={isSelected}
                                      onChange={() => handleUnitSelect(unitId)}
                                    />
                                  </div>
                                  <div className="unit-card-title">
                                    <div className="unit-card-name">
                                      <span className="unit-number">{t('contract.create.fields.unit') || 'Unit'} {unit.unitNumber || unit.UnitNumber || unitId}</span>
                                      {(unit.unitType || unit.UnitType) && (
                                        <span className="unit-type">{(unit.unitType?.nameEng || unit.UnitType?.NameEng || unit.unitType?.NameEng || unit.UnitType?.nameEng)}</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="unit-card-body">
                                  <div className="unit-card-details-grid">
                                    {(unit.unitArea || unit.UnitArea) && (
                                      <div className="unit-detail-item">
                                        <i className="fas fa-ruler-combined"></i>
                                        <span><strong>{t('contract.create.fields.area') || 'Area'}:</strong> {unit.unitArea || unit.UnitArea} m²</span>
                                      </div>
                                    )}
                                    {(unit.floorNumber || unit.FloorNumber) && (
                                      <div className="unit-detail-item">
                                        <i className="fas fa-layer-group"></i>
                                        <span><strong>{t('contract.create.fields.unitDetails.floor') || 'Floor'}:</strong> {unit.floorNumber || unit.FloorNumber}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Property Information Fields */}
                <div className="card mb-4">
                  <div className="card-header">
                    <h5><i className="fas fa-info-circle text-primary"></i> <span className="text-primary">{t('annualReconciliation.create.sections.propertyInformation') || 'Property Information'}</span></h5>
                  </div>
                  <div className="card-body">
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">
                          <i className="fas fa-building"></i> {t('annualReconciliation.create.fields.propertyType')}
                        </label>
                        <div className="input-wrapper">
                          <i className="fas fa-building input-icon"></i>
                          <input
                            type="text"
                            className="form-input"
                            value={propertyType}
                            onChange={(e) => setPropertyType(e.target.value)}
                            placeholder={t('annualReconciliation.create.fields.propertyType')}
                            maxLength={100}
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          <i className="fas fa-map-marked-alt"></i> {t('annualReconciliation.create.fields.propertyDistrict')}
                        </label>
                        <div className="input-wrapper">
                          <i className="fas fa-map-marked-alt input-icon"></i>
                          <input
                            type="text"
                            className="form-input"
                            value={propertyDistrict}
                            onChange={(e) => setPropertyDistrict(e.target.value)}
                            placeholder={t('annualReconciliation.create.fields.propertyDistrict')}
                            maxLength={255}
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          <i className="fas fa-hashtag"></i> {t('annualReconciliation.create.fields.plotNumber')}
                        </label>
                        <div className="input-wrapper">
                          <i className="fas fa-hashtag input-icon"></i>
                          <input
                            type="text"
                            className="form-input"
                            value={plotNumber}
                            onChange={(e) => setPlotNumber(e.target.value)}
                            placeholder={t('annualReconciliation.create.fields.plotNumber')}
                            maxLength={50}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="tab-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate('/annual-reconciliation')}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleNextTab}
                  >
                    {t('common.next') || 'Next'} <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            )}

            {/* Tab 2: Lessor (First Party) */}
            {activeTab === 2 && (
              <div className="tab-panel">
                <h3 className="tab-title">{t('annualReconciliation.create.sections.firstParty') || 'Lessor (First Party)'}</h3>
                
                {!propertyId ? (
                  <div className="alert alert-warning">
                    <i className="fas fa-exclamation-triangle"></i> {t('contract.create.messages.pleaseSelectPropertyFirst') || 'Please select a property first.'}
                  </div>
                ) : propertyOwnerships.length === 0 ? (
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle"></i> {t('contract.create.fields.noPropertyOwnerships') || 'No property ownerships available.'}
                  </div>
                ) : (
                  <div className="card mb-4">
                    <div className="card-header">
                      <h5><i className="fas fa-user-tie text-primary"></i> <span className="text-primary">{t('contract.create.fields.selectLessor') || 'Select Lessor'}</span></h5>
                    </div>
                    <div className="card-body">
                      <div className="table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th></th>
                              <th>{t('contract.create.tables.nameEnglish') || 'Name (English)'}</th>
                              <th>{t('contract.create.tables.nameArabic') || 'Name (Arabic)'}</th>
                              <th>{t('contract.create.tables.identityNumber') || 'Identity Number'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {propertyOwnerships.map((ownership) => {
                              const ownershipId = ownership.id || ownership.Id;
                              const isSelected = lessor?.propertyOwnershipId === ownershipId;
                              return (
                                <tr key={ownershipId}>
                                  <td>
                                    <input
                                      type="radio"
                                      name="selectedLessor"
                                      checked={isSelected}
                                      onChange={() => handleLessorSelect(ownershipId)}
                                    />
                                  </td>
                                  <td>{ownership.ownershipNameEng || ownership.OwnershipNameEng || t('common.notAvailable')}</td>
                                  <td>{ownership.ownershipNameAr || ownership.OwnershipNameAr || '-'}</td>
                                  <td>{ownership.identityNumber || ownership.IdentityNumber || '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-user"></i> {t('annualReconciliation.create.fields.firstPartyName')} <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <i className="fas fa-user input-icon"></i>
                      <input
                        type="text"
                        className={`form-input ${errors.firstPartyName ? 'is-invalid' : ''}`}
                        value={firstPartyName}
                        onChange={(e) => setFirstPartyName(e.target.value)}
                        placeholder={t('annualReconciliation.create.fields.firstPartyName')}
                        maxLength={255}
                        required
                      />
                    </div>
                    {errors.firstPartyName && (
                      <div className="error-message">{errors.firstPartyName}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-id-card"></i> {t('annualReconciliation.create.fields.firstPartyIdNumber')} <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <i className="fas fa-id-card input-icon"></i>
                      <input
                        type="text"
                        className={`form-input ${errors.firstPartyIdNumber ? 'is-invalid' : ''}`}
                        value={firstPartyIdNumber}
                        onChange={(e) => setFirstPartyIdNumber(e.target.value)}
                        placeholder={t('annualReconciliation.create.fields.firstPartyIdNumber')}
                        maxLength={50}
                        required
                      />
                    </div>
                    {errors.firstPartyIdNumber && (
                      <div className="error-message">{errors.firstPartyIdNumber}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-flag"></i> {t('annualReconciliation.create.fields.firstPartyNationality')}
                    </label>
                    <div className="input-wrapper">
                      <i className="fas fa-flag input-icon"></i>
                      <input
                        type="text"
                        className="form-input"
                        value={firstPartyNationality}
                        onChange={(e) => setFirstPartyNationality(e.target.value)}
                        placeholder={t('annualReconciliation.create.fields.firstPartyNationality')}
                        maxLength={100}
                      />
                    </div>
                  </div>
                </div>

                <div className="tab-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handlePreviousTab}
                  >
                    <i className="fas fa-arrow-left"></i> {t('common.previous') || 'Previous'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleNextTab}
                  >
                    {t('common.next') || 'Next'} <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            )}

            {/* Tab 3: Tenant (Second Party) */}
            {activeTab === 3 && (
              <div className="tab-panel">
                <h3 className="tab-title">{t('annualReconciliation.create.sections.secondParty') || 'Tenant (Second Party)'}</h3>
                
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-user"></i> {t('annualReconciliation.create.fields.secondPartyName')}
                    </label>
                    <div className="input-wrapper">
                      <i className="fas fa-user input-icon"></i>
                      <input
                        type="text"
                        className="form-input"
                        value={secondPartyName}
                        onChange={(e) => setSecondPartyName(e.target.value)}
                        placeholder={t('annualReconciliation.create.fields.secondPartyName')}
                        maxLength={255}
                      />
                    </div>
                  </div>
                </div>

                <div className="tab-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handlePreviousTab}
                  >
                    <i className="fas fa-arrow-left"></i> {t('common.previous') || 'Previous'}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? t('common.loading') : t('annualReconciliation.create.save')}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>

      <SuccessModal
        show={showSuccessModal}
        message={t('annualReconciliation.create.messages.createSuccess')}
        onClose={() => {
          setShowSuccessModal(false);
        }}
      />

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default AnnualReconciliationCreate;
